import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { AppState } from '@nxsys/store';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Cell,
  Label,
  Pie,
  PieChart,
  Tooltip,
} from 'recharts';

export interface PayBreakdownItem {
  name: string;
  value: number;
  color: string;
  percentage: number;
}

type Props = {
  data: PayBreakdownItem[];
  total: number;
  totalLabel: string;
};

const PayBreakdownChart = ({ data, total, totalLabel }: Props) => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const chartData = data.map((item) => ({
    name: item.name,
    value: item.value,
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const activeData = payload[0];
      const item = data.find(
        (d: PayBreakdownItem) => d.name === activeData.name,
      );
      return (
        <div className="bg-neutral-900 text-white rounded-[8px] p-[12px] shadow-lg">
          <div className="flex items-center gap-x-[8px] mb-[4px]">
            <div
              className="w-[8px] h-[8px] rounded-full"
              style={{ backgroundColor: item?.color }}
            />
            <div className="text-[14px] font-[500]">{activeData.name}</div>
          </div>
          <div className="text-[14px] font-[600]">
            {formatMoney(
              activeData.value,
              showCurrencyByCountry(payrollCountry),
            )}{' '}
            <span style={{ color: item?.color }}>({item?.percentage}%)</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderLabel = () => {
    return null; // We don't want labels on the chart itself
  };

  return (
    <div className="PayBreakdownChart">
      <div className="flex justify-center items-center mb-[32px] relative">
          <PieChart width={160} height={160}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={65}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
              label={renderLabel}
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {chartData.map((_, index) => {
                const item = data[index];
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={item.color}
                    stroke={activeIndex === index ? item.color : 'none'}
                    strokeWidth={activeIndex === index ? 3 : 0}
                  />
                );
              })}
              <Label
                value={formatMoney(
                  total,
                  showCurrencyByCountry(payrollCountry),
                )}
                position="centerTop"
                fontSize="18px"
                fontWeight={600}
                fill={defaultTheme.theme.palette.neutral[700]}
              />
              <Label
                value={totalLabel}
                position="centerBottom"
                fontSize="12px"
                style={{ transform: 'translateY(-10px)' }}
                fontWeight={400}
                fill={defaultTheme.theme.palette.neutral[500]}
              />
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
      </div>
    </div>
  );
};

export default PayBreakdownChart;
